import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import customerService from '../../services/customerService';

/**
 * Customer sign-in — email and password, with the reset link that doubles as
 * "I bought something but never made an account".
 *
 * ONE FLOW, TWO MEANINGS. Someone who bought points and never set a password
 * still has a live balance; the same reset link lets them set their first one.
 * The copy says "get a link" rather than "reset your password" so it reads
 * correctly in both cases.
 *
 * ⚠️ The reset confirmation NEVER says whether the address was found. The server
 * answers identically either way, and contradicting it here would turn this form
 * into a way to discover who holds points with this host.
 */

interface CustomerLoginFormProps {
  /** Rendered above the fields — e.g. "Already have a plan?". */
  heading?: string;
  onSignedIn?: () => void;
  onCancel?: () => void;
}

const CustomerLoginForm: React.FC<CustomerLoginFormProps> = ({
  heading,
  onSignedIn,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { signIn } = useCustomerAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      onSignedIn?.();
    } catch {
      setError(
        t('booking:customer.invalidCredentials', 'That email and password did not match.')
      );
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!email.trim()) {
      setError(t('booking:customer.enterEmailFirst', 'Enter your email address first.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await customerService.requestPasswordReset(email.trim());
    } catch {
      // Deliberately swallowed: a failure here must not reveal anything about
      // the address either.
    } finally {
      // Always the same outcome, whatever happened.
      setResetSent(true);
      setBusy(false);
    }
  };

  if (resetSent) {
    return (
      <div className="space-y-3">
        <h3 className="text-base font-medium text-gray-900">
          {t('booking:customer.checkEmailTitle', 'Check your email')}
        </h3>
        <p className="text-sm text-gray-600">
          {t(
            'booking:customer.checkEmailBody',
            'If that address has points or a plan with us, we have sent a link to set your password.'
          )}
        </p>
        <button type="button" className="btn-secondary" onClick={() => setResetSent(false)}>
          {t('common:actions.back', 'Back')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSignIn} className="space-y-4">
      {heading && <h3 className="text-base font-medium text-gray-900">{heading}</h3>}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('common:form.email', 'Email')}
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input-base w-full"
          autoComplete="email"
          disabled={busy}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('common:form.password', 'Password')}
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input-base w-full"
          autoComplete="current-password"
          disabled={busy}
          required
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy
            ? t('common:status.loading', 'Loading...')
            : t('booking:customer.signIn', 'Sign in')}
        </button>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            {t('common:actions.cancel', 'Cancel')}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={handleReset}
        className="text-sm text-primary-600 hover:text-primary-700 underline"
        disabled={busy}
      >
        {t(
          'booking:customer.noPassword',
          'First time here, or forgot your password? Get a link'
        )}
      </button>
    </form>
  );
};

export default CustomerLoginForm;
